// Angular imports
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

// 3rd party imports
import notify from 'devextreme/ui/notify';

// CC-Common imports
import { ConvertUtils, IconConstants, Utils } from 'cc-common';

// Service imports
import { UserService } from '../../_services/data-services/user.service';
import { QaUserService } from '../../_services/data-services/qa-user.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { HelperService } from '../../_services/shared-services/helper.service';

// Model imports
import { ActionAccessResultDto } from '../../_models/action-access-result-dto';
import { QaTesterRawDto } from '../../_models/qa-tester-raw-dto';
import { QaTestersSaveParameterDto } from '../../_models/qa-testers-save-parameter-dto';

// Constant imports
import { MessageInterval } from '../../_constants/messages-intervals';

@Component({
    selector: 'app-manage-qa-users',
    templateUrl: './manage-qa-users.component.html',
    styleUrls: ['./manage-qa-users.component.scss']
})
export class ManageQaUsersComponent {
    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    iconClose = IconConstants.CLOSE;
    iconSave = IconConstants.SAVE;
    loadingVisible = false;

    qaModules: ActionAccessResultDto[];
    qaUsers: QaTesterRawDto[];

    constructor(
        private readonly userService: UserService,
        private readonly qaUserService: QaUserService,
        private readonly errorService: ServerErrorService,
        private readonly helperService: HelperService
    ) {}

    onShown(e: any): void {
        this.populateBetaModules(true);
        e.component.repaint();
    }

    onHidden(): void {
        this.visible = false;
        this.visibleChange.emit(this.visible);
    }

    onToolbarPreparing(e: any): void {
        e.toolbarOptions.items = [];
    }

    onRowValueChanged(e: any, row: any): void {
        if (
            e.previousValue.length > e.value.length &&
            Utils.notEqualsIgnoreCase(e.event.currentTarget.className, 'dx-tag-remove-button')
        ) {
            e.value = e.previousValue; // Hack to ignore change on duplicate entry
        }
        row.setValue(e.value);
        row.data.userIds = ConvertUtils.listToString(e.value);
        row.data.isUpdated = true;

        /* Logic for providing new access */
        const newlyAddedUserIds = e.value.filter((item) => Utils.notInList(item, e.previousValue));
        if (Utils.notNullOrEmpty(newlyAddedUserIds)) {
            if (Utils.notNullOrEmpty(row.data.parentCode)) {
                this.qaUsers.forEach((node) => {
                    if (Utils.equalsIgnoreCase(node.moduleCode, row.data.parentCode)) {
                        node.usersList.push(...newlyAddedUserIds);
                        node.usersList = Utils.uniqueList(node.usersList);
                        node.userIds = ConvertUtils.listToString(node.usersList);
                        node.isUpdated = true;
                    }
                });
            }
        }
        const removedUserIds = e.previousValue.filter((item) => Utils.notInList(item, e.value));
        /* Logic if access is removed. Fisrt check if the user has access to other module under same parent. If yes, keep the parent access. Other wise remove parentNode access */
        if (Utils.notNullOrEmpty(removedUserIds)) {
            this.qaUsers.forEach((node) => {
                if (Utils.equalsIgnoreCase(node.moduleCode, row.data.parentCode)) {
                    // Check if user has access to other module under same parent
                    const otherSubConfigurations = this.qaUsers.filter(
                        (subNode) =>
                            Utils.equalsIgnoreCase(subNode.parentCode, row.data.parentCode) &&
                            Utils.notEqualsIgnoreCase(subNode.moduleCode, row.data.moduleCode)
                    );

                    const hasAccessToOtherConfigurations = otherSubConfigurations.filter((otherSubNode) => {
                        const userIds = otherSubNode.usersList.filter((user) => Utils.isInList(user, removedUserIds));
                        if (Utils.notNullOrEmpty(userIds)) {
                            return otherSubNode;
                        }
                    });
                    if (Utils.isNullOrEmpty(hasAccessToOtherConfigurations)) {
                        node.usersList = node.usersList.filter((userId) => Utils.notInList(userId, removedUserIds));
                        node.usersList = Utils.uniqueList(node.usersList);
                        node.userIds = ConvertUtils.listToString(node.usersList);
                        node.isUpdated = true;
                    }
                }
            });
        }
    }

    onGridContentReady(event: any): void {
        event.component.filter(['visibility', '<>', false]);
    }

    onCloseClick(): void {
        const updatedRecords = this.qaUsers.filter((r) => r.isUpdated);
        if (Utils.notNullOrEmpty(updatedRecords)) {
            const dialog = this.helperService.getAgreementDialog(
                `Confirmation`,
                `There are one or more unsaved records!`,
                `Save & Close`,
                `Discard & Close`,
                `Cancel`,
                'success',
                'danger',
                'default'
            );
            dialog.show().then((chioce) => {
                if (Utils.isTrue(chioce)) {
                    this.onSaveClick();
                } else if (Utils.isFalse(chioce)) {
                    this.onHidden();
                }
            });
        } else {
            this.onHidden();
        }
    }

    onSaveClick(): void {
        const updatedRecords = this.qaUsers.filter((r) => r.isUpdated);
        if (Utils.notNullOrEmpty(updatedRecords)) {
            this.loadingVisible = true;
            const params = new QaTestersSaveParameterDto();
            params.qaTesters = updatedRecords;
            this.qaUserService.saveQaMembers(params).subscribe(
                () => {
                    notify('Saved QA User successfully.', 'success', MessageInterval.successMsgInterval);
                    this.populateQaUsers(true);
                    this.onHidden();
                },
                (error: HttpErrorResponse) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
        } else {
            this.errorService.updateSubjectErrorMessage(`There are no changes to Save!`);
        }
    }

    private populateBetaModules(clearCache = false): void {
        this.loadingVisible = true;
        this.userService.getQaActionList().subscribe(
            (results: ActionAccessResultDto[]) => {
                this.qaModules = results;
                this.populateQaUsers(clearCache);
            },
            (error: HttpErrorResponse) => {
                this.errorService.displayError(error);
            }
        );
    }

    private populateQaUsers(clearCache = false): void {
        this.qaUserService.getQaUsers(clearCache).subscribe(
            (results: QaTesterRawDto[]) => {
                this.qaUsers = this.qaModules.map((m) => {
                    const rec = new QaTesterRawDto();
                    rec.moduleCode = m.code;
                    rec.parentCode = Utils.notNullOrEmpty(m.parentCode) ? m.parentCode : null;
                    rec.visibility = m.visibility;
                    rec.usersList = [];
                    const res = results.find((r) => Utils.equalsIgnoreCase(r.moduleCode, m.code));
                    if (Utils.notNullAndDefined(res)) {
                        rec.usersList = ConvertUtils.stringToList(res.userIds);
                    }
                    return rec;
                });
                this.loadingVisible = false;
            },
            (error: HttpErrorResponse) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }
}
